/**
 * Tag Suggestions API Hooks (Phase 22-23: FR-089 to FR-112)
 * TanStack Query hooks for AI-powered tag suggestions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { syncedLocationKeys } from './synced-locations';
import type {
  TagSuggestionsResponse,
  AnalyzeTagsResponse,
  AcceptRejectTagsRequest,
  AcceptRejectTagsResponse,
  TagScope,
  AddRemoveTagRequest,
  AvailableTagsResponse,
} from '@/types/api';

// Re-export types for consumers of this API
export type { AvailableTag, TagScope } from '@/types/api';

export const tagSuggestionsKeys = {
  all: ['tagSuggestions'] as const,
  detail: (locationId: string) => [...tagSuggestionsKeys.all, 'detail', locationId] as const,
  availableTags: () => [...tagSuggestionsKeys.all, 'availableTags'] as const,
};

/**
 * Fetch tag suggestions for a synced location.
 * Polls while status is 'analyzing' to show real-time updates.
 */
export function useTagSuggestions(locationId: string) {
  return useQuery({
    queryKey: tagSuggestionsKeys.detail(locationId),
    queryFn: () =>
      api.get<TagSuggestionsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions`
      ),
    enabled: !!locationId,
    // Poll every 3 seconds while analyzing
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'analyzing') {
        return 3000;
      }
      return false;
    },
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Trigger manual tag analysis for a location.
 * FR-095: Admins can manually trigger re-analysis
 */
export function useAnalyzeTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      api.post<AnalyzeTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/analyze`
      ),
    onSuccess: (_, locationId) => {
      // Invalidate tag suggestions to start polling
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
      // Also invalidate the synced location to update any status indicators
      void queryClient.invalidateQueries({
        queryKey: syncedLocationKeys.detail(locationId),
      });
    },
  });
}

/**
 * Re-analyze only location text (title + description).
 * Preserves image tags.
 */
export function useReanalyzeLocationText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      api.post<AnalyzeTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/analyze-location`
      ),
    onSuccess: (_, locationId) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: syncedLocationKeys.detail(locationId),
      });
    },
  });
}

/**
 * Re-analyze a single image by index.
 * Preserves location tags and other image tags.
 */
export function useReanalyzeSingleImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, imageIndex }: { locationId: string; imageIndex: number }) =>
      api.post<AnalyzeTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/analyze-image/${String(imageIndex)}`
      ),
    onSuccess: (_, { locationId }) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: syncedLocationKeys.detail(locationId),
      });
    },
  });
}

/**
 * Re-analyze all images for a location.
 * Preserves location tags.
 */
export function useReanalyzeAllImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      api.post<AnalyzeTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/analyze-all-images`
      ),
    onSuccess: (_, locationId) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: syncedLocationKeys.detail(locationId),
      });
    },
  });
}

/**
 * Fetch available tags from ScoutAround taxonomy.
 * Phase 26: Used for tag selector dropdown
 */
export function useAvailableTags() {
  return useQuery({
    queryKey: tagSuggestionsKeys.availableTags(),
    queryFn: async () => {
      const response = await api.get<AvailableTagsResponse>('/api/v1/tags');
      return response.tags;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (same as backend cache)
    retry: 2,
  });
}

/**
 * Accept tags request parameters
 */
interface AcceptTagsParams {
  locationId: string;
  tagIds: number[];
  scope: TagScope;
  imageIndex?: number;
}

/**
 * Accept specific tags for a location.
 * FR-097: Move tags from suggestions to confirmed list
 */
export function useAcceptTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagIds, scope, imageIndex }: AcceptTagsParams) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/accept`,
        { tagIds, scope, imageIndex } satisfies AcceptRejectTagsRequest
      ),
    onSuccess: (_, { locationId }) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}

/**
 * Reject specific tags for a location.
 * FR-098: Add tags to rejected list
 */
export function useRejectTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagIds, scope, imageIndex }: AcceptTagsParams) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/reject`,
        { tagIds, scope, imageIndex } satisfies AcceptRejectTagsRequest
      ),
    onSuccess: (_, { locationId }) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}

/**
 * Accept all pending tags for a location.
 * Bulk operation to confirm all suggestions.
 */
export function useAcceptAllTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/accept-all`
      ),
    onSuccess: (_, locationId) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}

/**
 * Reject all pending tags for a location.
 * Bulk operation to reject all suggestions.
 */
export function useRejectAllTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/reject-all`
      ),
    onSuccess: (_, locationId) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}

/**
 * Add manual tag request parameters
 * Phase 26: Manual Tag Management
 */
interface AddManualTagParams {
  locationId: string;
  tagId: number;
  scope: TagScope;
  imageIndex?: number;
}

/**
 * Add a manual tag to a location.
 * Phase 26: Manual Tag Management
 */
export function useAddManualTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagId, scope, imageIndex }: AddManualTagParams) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/add`,
        { tagId, scope, imageIndex } satisfies AddRemoveTagRequest
      ),
    onSuccess: (_, { locationId }) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}

/**
 * Remove tag request parameters
 * Phase 26: Manual Tag Management
 */
interface RemoveTagParams {
  locationId: string;
  tagId: number;
  scope: TagScope;
  imageIndex?: number;
}

/**
 * Remove a tag from a location (manual or AI-confirmed).
 * Phase 26: Manual Tag Management
 */
export function useRemoveTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagId, scope, imageIndex }: RemoveTagParams) =>
      api.post<AcceptRejectTagsResponse>(
        `/api/v1/synced-locations/${locationId}/tag-suggestions/remove`,
        { tagId, scope, imageIndex } satisfies AddRemoveTagRequest
      ),
    onSuccess: (_, { locationId }) => {
      void queryClient.invalidateQueries({
        queryKey: tagSuggestionsKeys.detail(locationId),
      });
    },
  });
}
